<template>
	<div class="sidebar" :data-color="activeColor" :data-image="backgroundImage" :data-background-color="backgroundColor" :style="sidebarStyle">
		<div class="header" :style="{ height: isEditing ? '100%' : 'auto' }">
			<img class="company-logo" src="/img/logo-final-gerenciador.png" alt="Gerenciador" />
			<div v-if="loading" style="width: 40px; margin: 25px auto">
				<circle2 background="#999" color="#0C4DA2"></circle2>
			</div>

			<template v-else>
				<div class="user-info">
					<img :src="user.profilePictureUrl" :alt="user.name" /><br />
					<label>
						<input type="file" class="file-input" @change="uploadFile($event.target.files)" />
						<div v-if="isEditing" class="change-picture"><md-icon>edit</md-icon></div>
					</label>
					<span class="user-name">{{ user.name }}</span
					><br />
					<span class="user-role">Perfil de acesso: </span><span class="user-role-role">{{ translatedRole }}</span
					><br />
					<span class="user-email">{{ user.email }}</span>
				</div>

				<div v-if="isEditing" class="md-layout" style="margin-top: 15px">
					<form-field class="md-layout-item md-size-100" fieldKey="currentPassword" :schema="userSchema" :entity="passwordEntity"></form-field>
					<form-field class="md-layout-item md-size-100" fieldKey="password" :schema="userSchema" :entity="passwordEntity"></form-field>
					<form-field class="md-layout-item md-size-100" fieldKey="passwordConfirmation" :schema="userSchema" :entity="passwordEntity"></form-field>
					<div class="md-layout-item md-size-100" style="margin-top: 15px">
						<md-button class="md-accent" @click="updateInfo()">Atualizar dados</md-button>
					</div>
					<div class="md-layout-item md-size-100" style="margin-top: 15px">
						<h2 style="margin: 0; font-size: 15px; cursor: pointer" @click="clearEdit()">Cancelar</h2>
					</div>
				</div>
			</template>

			<div class="pulldown">
				<div class="pulldown-button" @click="isEditing = !isEditing" style="cursor: pointer">
					<md-icon>{{ isEditing ? "expand_less" : "expand_more" }}</md-icon>
				</div>
			</div>
		</div>

		<div v-if="!isEditing" class="sidebar-wrapper" ref="sidebarScrollArea">
			<div class="nav md-layout">
				<slot name="links"></slot>
			</div>
			<hr />
			<div class="logout">
				<a href="#" v-on:click="logout">
					<md-icon>input</md-icon>
					<p>Deslogar</p>
				</a>
			</div>
		</div>
	</div>
</template>
<script>
import { Circle2 } from "vue-loading-spinner";

import { getItem, setItem } from "@/models/session";
import { uploadFile } from "@/models/request";
import userModel from "@/models/user.model";

import FormField from "@/layouts/DashboardLayoutComponents/FormField.vue";

export default {
	name: "sidebar",
	components: { Circle2, FormField },
	props: {
		title: {
			type: String,
			default: "Gerenciador",
		},
		activeColor: {
			type: String,
			default: "accent",
			validator: value => {
				let acceptedValues = ["", "primary", "accent", "azure", "green", "orange", "danger", "rose"];
				return acceptedValues.indexOf(value) !== -1;
			},
		},
		backgroundImage: {
			type: String,
			default: "./img/sidebar-2.jpg",
		},
		backgroundColor: {
			type: String,
			default: "primary",
			validator: value => {
				let acceptedValues = ["", "primary", "accent", "black", "white", "red"];
				return acceptedValues.indexOf(value) !== -1;
			},
		},
		logo: {
			type: String,
			default: "./img/vue-logo.png",
		},
		sidebarLinks: {
			type: Array,
			default: () => [],
		},
		autoClose: {
			type: Boolean,
			default: true,
		},
	},
	provide() {
		return {
			autoClose: this.autoClose,
		};
	},
	methods: {
		minimizeSidebar() {
			if (this.$sidebar) {
				this.$sidebar.toggleMinimize();
			}
		},
		logout() {
			return userModel
				.logout()
				.then(() => this.$router.push("/login"))
				.catch(error => console.error(error));
		},
		async uploadFile(files) {
			this.loading = true;
			try {
				const { uuid, url } = await uploadFile(files[0]);
				const user = await userModel.updateProfilePicture(uuid);
				setItem("user", user);
				this.user = user;
			} catch (error) {
				console.error(error);
				this.$toasted.error("Não foi possível enviar o arquivo. Tente novamente.");
			}
			this.loading = false;
		},
		clearEdit() {
			this.passwordEntity.password = "";
			this.passwordEntity.passwordConfirmation = "";
			this.passwordEntity.currentPassword = "";
			this.isEditing = false;
		},
		async updateInfo() {
			if (!this.passwordEntity.password || !this.passwordEntity.passwordConfirmation || !this.passwordEntity.currentPassword) {
				return this.$toasted.error("Preencha todos os campos");
			}
			if (this.passwordEntity.password.length < 6) {
				return this.$toasted.error("A senha deve ter pelo menos 6 caracteres");
			}
			if (this.passwordEntity.password !== this.passwordEntity.passwordConfirmation) {
				return this.$toasted.error("As senhas não conferem");
			}
			this.loading = true;
			try {
				await userModel.updatePassword({ password: this.passwordEntity.password, currentPassword: this.passwordEntity.currentPassword });
				await this.$toasted.success("Senha atualizada com sucesso");
				this.clearEdit();
			} catch (error) {
				console.error(error);
				await this.$toasted.error(error.friendlyMessage || "Senha atual incorreta");
			}
			this.loading = false;
		},
	},
	computed: {
		sidebarStyle() {
			return {
				width: "279px",
				backgroundImage: `url(${this.backgroundImage})`,
			};
		},
		translatedRole() {
			const roleTranslations = {
				'PROFESSIONAL': 'Profissional',
				'CLIENT': 'Cliente',
				'OPERATOR': 'Operador de escala',
				'OPERATOR2': 'Operador de escala II',
				'FINANCE': 'Financeiro',
				'FINANCE2': 'Financeiro II',
				'COORDINATOR': 'Coordenador',
				'COORDINATOR2': 'Coordenador II',
				'MASTER': 'Administrador',
				'MASTER2': 'Administrador II'
			};
			return roleTranslations[this.user.role] || this.user.role;
		},
	},
	beforeDestroy() {
		if (this.$sidebar.showSidebar) {
			this.$sidebar.showSidebar = false;
		}
	},
	data: function() {
		return {
			user: {
				name: "Carregando...",
				role: "Carregando...",
				email: "Carregando...",
				profilePictureUrl: "/img/default-avatar.png",
			},
			loading: false,
			isEditing: false,
			userSchema: [],
			passwordEntity: {
				currentPassword: "",
				password: "",
				passwordConfirmation: "",
			},
		};
	},
	async mounted() {
		this.user = getItem("user");
		if (!this.user.profilePictureUrl) {
			this.user.profilePictureUrl = "/img/default-avatar.png";
		}
		this.userSchema = await userModel.getDescription();
	},
};
</script>
<style lang="scss">
@media (min-width: 992px) {
	.navbar-search-form-mobile,
	.nav-mobile-menu {
		display: none;
	}
}
.sidebar-wrapper {
	overflow-x: hidden !important;

	hr {
		margin: 40px;
		border: 1px solid #eee;
	}
}
.nav.md-layout {
	list-style: none;
}
.header {
	position: relative;
	z-index: 4;
	text-align: center;
	background-color: #f8f8f8;
	padding: 35px 20px;

	.company-logo {
		width: 150px;
	}

	.user-info {
		margin-top: 30px;

		img {
			width: 100px;
			height: 100px;
			border-radius: 50%;
			margin-bottom: 10px;
			border: 3px solid #0c4da2;
		}

		.file-input {
			width: 0.1px;
			height: 0.1px;
			opacity: 0;
			overflow: hidden;
			position: absolute;
			z-index: -1;
		}

		.change-picture {
			position: relative;
			background-color: #0c4da2;
			border-radius: 50%;
			width: 30px;
			height: 30px;
			margin-top: -35px;
			margin-bottom: 5px;
			left: 55%;
			cursor: pointer;

			.md-icon {
				font-size: 1.1rem !important;
				color: #fff !important;
				opacity: 1 !important;
			}
		}

		.user-name {
			font-weight: 700;
		}
		.user-role {
			font-size: 0.9rem;
			font-weight: 500;
		}
		.user-role-role {
			font-size: 0.9rem;
			font-weight: 800;
		}
		.user-email {
			font-weight: 400;
			font-size: 0.9rem;
		}
	}

	.pulldown {
		position: absolute;
		left: 50%;
		bottom: 0;
	}
	.pulldown-button {
		position: relative;
		left: -50%;
		width: 120px;
		height: 15px;
		background-color: #0c4da2;
		border-radius: 7px 7px 0 0;
		padding-top: -10px;

		i {
			opacity: 1 !important;
			color: #fff !important;
			margin-top: -12px !important;
		}
	}
}

.logout {
	margin-top: 40px;
	i.md-icon.md-theme-default.md-icon-font {
		color: #333 !important;
	}
	p {
		font-size: 0.8rem;
		font-weight: 700;
		color: #333 !important;
		margin-top: 0;
	}
	text-align: center;
}
</style>
